import { useCallback } from 'react';
import * as THREE from 'three';
import { setCameraView, runViewAnalysisFromFacadePoint, removeNearbyViewAnalysis } from '@/utils/viewAnalysis';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { handleGridPointClick } from '@/utils/gridSelection';
import { deleteRectangularHole } from '@/utils/buildingWindows';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { useWindowSettingsStore } from '@/store/windowSettingsStore';
import { useViewFeaturesStore } from '@/store/viewFeaturesStore';
import { markBuildingAsLandmark, removeLandmarkFromBuilding, removePaintedAreaVisualization, showPaintedAreaHandles, removePaintedAreaHandles } from '@/utils/viewFeatures';
import { AnalysisTab } from '@/components/home/home';

export interface ClickHandlerHook {
	createClickHandler: (
		camera: THREE.Camera,
		scene: THREE.Scene
	) => (event: MouseEvent, isRotating: boolean) => void;
}

export interface ClickHandlerParams {
	threeCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
	starsRef: React.MutableRefObject<THREE.Points | null>;
	isHoleCreationMode: boolean;
	isHoleDeletionMode: boolean;
	currentSunPositionRef: React.MutableRefObject<THREE.Vector3>;
	directionalLightRef: React.MutableRefObject<THREE.DirectionalLight | null>;
	ambientLightRef: React.MutableRefObject<THREE.AmbientLight | null>;
	tab: AnalysisTab;
	onBuildingClick?: (building: THREE.Object3D) => void;
	rendererRef?: React.MutableRefObject<THREE.WebGLRenderer | null>;
}

export const useClickHandler = (params: ClickHandlerParams): ClickHandlerHook => {
	const { threeCanvasRef, starsRef, isHoleCreationMode, isHoleDeletionMode, tab } = params;

	const pauseEvents = () => {
		useThreeSceneStore.getState().setAreEventsPaused(true);
	};

	const resumeEvents = () => {
		useThreeSceneStore.getState().setAreEventsPaused(false);
	};

	const createClickHandler = useCallback(
		(camera: THREE.Camera, scene: THREE.Scene) => {
			const raycaster = new THREE.Raycaster();
			const mouse = new THREE.Vector2();
			let lastClickTime = 0;
			let isProcessingClick = false;

			return (event: MouseEvent, isRotating: boolean) => {			// Only respond to left-click (button 0)
			
				if (event.button !== 0) return;
				// Get landmark and paint mode states
				const { isLandmarkSelectionMode, isLandmark, isPaintDeletionMode, removePaintedArea, selectedPaintedAreaId, setSelectedPaintedAreaId } = useViewFeaturesStore.getState();
				// Prevent duplicate clicks within 200ms
				const currentTime = Date.now();
				if (currentTime - lastClickTime < 200 || isProcessingClick) {
					return;
				}
				if (isRotating) {
					return;
				}

				isProcessingClick = true;
				lastClickTime = currentTime;

				try {
					const canvas = threeCanvasRef.current!;
					const rect = canvas.getBoundingClientRect();

					// Convert to normalized device coordinates
					mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
					mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

					raycaster.setFromCamera(mouse, camera);

					// Filter objects for intersection (exclude shadow indicators and lux tooltips)
					const intersectObjects = scene.children.filter(
						(child) =>
							child !== starsRef.current &&
							child.type !== 'DirectionalLight' &&
							child.type !== 'AmbientLight' &&
							child.type !== 'CameraHelper' &&
							child.type !== 'LineSegments' &&
							!child.userData.ignoreSceneClick &&
							!child.userData.isShadowIndicator && // Exclude existing spheres
							!child.userData.isLuxTooltip // Exclude lux tooltips
					);

					const intersects = raycaster
						.intersectObjects(intersectObjects, true)
						.filter((intersect) => !intersect.object.userData.ignoreSceneClick);

					if (intersects.length > 0) {
						const clickPoint = intersects[0].point;
						const intersectedObject = intersects[0].object;
						const windowObject = intersects.find((intersect) => intersect.object.userData.category === "window")?.object;
						let isClipped = false;
						const material = (intersectedObject as THREE.Mesh)
							.material as THREE.Material & { clippingPlanes?: THREE.Plane[] };

						if (material && Array.isArray(material.clippingPlanes)) {
							isClipped = material.clippingPlanes.some(
								(plane: THREE.Plane) => plane.distanceToPoint(clickPoint) < 0
							);
						}

						let finalClickPoint = clickPoint;
						if (isClipped) {
							const raycasterDown = new THREE.Raycaster(
								clickPoint,
								new THREE.Vector3(0, 0, -1),
								0,
								1000
							);
							const downIntersects = raycasterDown.intersectObject(
								intersectedObject,
								true
							);

							for (const hit of downIntersects) {
								const hitPoint = hit.point;
								const hitMaterial = (hit.object as THREE.Mesh)
									.material as THREE.Material & {
									clippingPlanes?: THREE.Plane[];
								};
								const hitClipped = hitMaterial?.clippingPlanes?.some(
									(plane: THREE.Plane) => plane.distanceToPoint(hitPoint) < 0
								);
								if (!hitClipped) {
									finalClickPoint = hitPoint;
									break;
								}
							}
						}

						if (isHoleCreationMode && event.button === 0) {
							// Click-to-place is disabled; window placement is drag-only.
							return;
						} else if (isHoleDeletionMode && event.button === 0) {
							pauseEvents();
							// Collect all windows that contain the click point
							const windowsToDelete: THREE.Mesh[] = [];

							scene.traverse((child) => {
								if (child.userData.isWindow) {
									const windowMesh = child as THREE.Mesh;
									const windowBox = new THREE.Box3().setFromObject(windowMesh)
									if (windowBox.containsPoint(finalClickPoint)) {
										windowsToDelete.push(windowMesh);
									}
								}
							});

							if (windowsToDelete.length === 0) {
								const highlightedWindowUUID = useWindowSettingsStore.getState().highlightedWindowUUID;
								if (highlightedWindowUUID) {
									scene.traverse((child) => {
										if (child.userData.isWindow && child.userData.windowUuid === highlightedWindowUUID) {
											windowsToDelete.push(child as THREE.Mesh);
										}
									});
								}
							}

							if (windowsToDelete.length === 0) {
								console.warn('No windows found for deletion after checking highlighted window.');
								resumeEvents();
								return;
							}

							// Delete windows after traversal is complete
							for (const windowMesh of windowsToDelete) {
								deleteRectangularHole(windowMesh, scene, () =>
									console.log('Hole deleted')
								)
									.then(() => {
										resumeEvents();
									})
									.catch((error) => {
										resumeEvents();
										console.error('Error deleting hole:', error);
									}).finally(() => {
										resumeEvents();
									});
						}
					} else if (isLandmarkSelectionMode && event.button === 0) {
						const buildingIntersect = intersects.find(
							(intersect) => intersect.object.userData.category === 'building'
						);

						if (buildingIntersect) {
							const building = buildingIntersect.object;
						
						// Find the root building object (go up the hierarchy)
						let rootBuilding: THREE.Object3D | null = building;
						while (rootBuilding && rootBuilding.parent && rootBuilding.parent.type !== 'Scene') {
							rootBuilding = rootBuilding.parent;
						}

						if (rootBuilding) {
							// Toggle landmark status
							if (isLandmark(rootBuilding.uuid)) {
								removeLandmarkFromBuilding(rootBuilding, scene);
							} else {
								markBuildingAsLandmark(rootBuilding, scene);
							}
						}
					}
				} else if (isPaintDeletionMode && event.button === 0) {
					// Handle painted area deletion
					const paintedAreaIntersect = intersects.find(
						(intersect) => intersect.object.userData.isPaintedArea
					);

					if (paintedAreaIntersect) {
						const areaId = paintedAreaIntersect.object.userData.paintedAreaId;
						
						// Remove from store
						removePaintedArea(areaId);
						
						// Remove visualization from scene
						removePaintedAreaVisualization(areaId, scene);
					}
				} else {
					// Check if clicking on a handle (don't deselect if so)
					const handleIntersect = intersects.find(
						(intersect) => intersect.object.userData.isHandle || intersect.object.userData.isHandleGlow
					);
					
					if (handleIntersect) {
						// Clicking on handle - do nothing, let drag handler take over
						return;
					}
					
					// Check if clicking on a painted area to edit it
					const paintedAreaIntersect = tab.id === "windows-openings"
						? intersects.find(
							(intersect) => intersect.object.userData.isPaintedArea
						)
						: null;

					if (paintedAreaIntersect && event.button === 0) {
						const areaId = paintedAreaIntersect.object.userData.paintedAreaId;
						
						if (selectedPaintedAreaId === areaId) {
							// Deselect
							setSelectedPaintedAreaId(null);
							removePaintedAreaHandles(scene);
						} else {
							setSelectedPaintedAreaId(areaId);
							showPaintedAreaHandles(areaId, scene);
						}
						return;
					} else if (selectedPaintedAreaId) {
						// Clicked elsewhere, deselect
						setSelectedPaintedAreaId(null);
						removePaintedAreaHandles(scene);
					}
				}
				if ((intersectedObject.userData.isViewPointTarget || windowObject) && !event.altKey) {
							if (camera.type !== 'PerspectiveCamera') {
								console.warn('Camera is not a PerspectiveCamera.');
								return;
							}

							const windowParent = windowObject ? windowObject.parent : null;
							const windowParentUUID = windowParent ? windowParent.userData.windowUuid : null;

							const targetViewPoint = scene.children.find((child) => {
								if (child.userData.isViewPointTarget && child.userData.windowUuid === windowParentUUID) {
									return child;
								}
							});

							const targetObject = intersectedObject.userData.isViewPointTarget ? intersectedObject : targetViewPoint;
							
							if (!targetObject) {
								console.warn('No target object found for camera view.');
								return;
							}

							setCameraView(
								camera as THREE.PerspectiveCamera,
								scene,
								targetObject
							);
						} else {
							// Handle left click for grid selection
							if (event.button === 0) {
								// Find sunlight visualization
								const roomSunlightVisualization: THREE.Object3D[] = [];
								scene.traverse((child) => {
									if (child.userData.isRoomSunlightVisualization) {
										roomSunlightVisualization.push(child);
									}
								});

								if (roomSunlightVisualization.length > 0) {
									raycaster.params.Points = { threshold: 0.5 };
									const intersectsSunlight = raycaster.intersectObjects(
										roomSunlightVisualization,
										false
									);

									for (const intersection of intersectsSunlight) {
										if (intersection.instanceId === undefined) continue;

										const instancedMesh =
											intersection.object as THREE.InstancedMesh;
										const matrix = new THREE.Matrix4();
										instancedMesh.getMatrixAt(intersection.instanceId, matrix);
										const position = new THREE.Vector3();
										position.setFromMatrixPosition(matrix);

										const material =
											instancedMesh.material as THREE.Material & {
												clippingPlanes?: THREE.Plane[];
											};

										let isClipped = false;
										if (material && Array.isArray(material.clippingPlanes)) {
											isClipped = material.clippingPlanes.some(
												(plane: THREE.Plane) =>
													plane.distanceToPoint(position) < 0
											);
										}

										if (!isClipped) {
											const gridSize =
												useDailySunlightStore.getState().gridSize || 1;
											handleGridPointClick(position, gridSize, scene);
											break;
										}
									}

									return;
								}
								
								const isFacadeViewAnalysisMode = useThreeSceneStore.getState().isFacadeViewAnalysisMode;
								if (isFacadeViewAnalysisMode) {
									const buildingIntersect = intersects.find(
										(intersect) => intersect.object.userData.category === 'building'
									);

									if (buildingIntersect && buildingIntersect.face) {
										if (camera.type !== 'PerspectiveCamera') {
											console.warn('Camera is not a PerspectiveCamera.');
											return;
										}

										const clickPoint = buildingIntersect.point;
										
										const wasRemoved = removeNearbyViewAnalysis(clickPoint, scene, 1.0);
										
										if (wasRemoved) {
											return;
										}
										
										const faceNormal = buildingIntersect.face.normal.clone();
										
										const buildingObject = buildingIntersect.object;
										if (buildingObject instanceof THREE.Mesh) {
											const normalMatrix = new THREE.Matrix3().getNormalMatrix(buildingObject.matrixWorld);
											faceNormal.applyMatrix3(normalMatrix).normalize();
										}
										
										pauseEvents();
										runViewAnalysisFromFacadePoint(
											camera as THREE.PerspectiveCamera,
											scene,
											clickPoint,
											{ x: faceNormal.x, y: faceNormal.y, z: faceNormal.z }
										)
											.then(() => {
												resumeEvents();
											})
											.catch((error) => {
												console.error('Error running facade view analysis:', error);
												resumeEvents();
											});
										
										return;
									}
								}
							}
						}

						if (
							params.onBuildingClick &&
							intersectedObject.userData.category === 'building' &&
							event.altKey === false &&
							event.button === 0
						) {
							params.onBuildingClick(intersectedObject);
						}
					}
				} catch (error) {
					console.error('Error processing click:', error);
				} finally {
					// Reset processing flag after a short delay
					setTimeout(() => {
						isProcessingClick = false;
					}, 100);
			}
			};
		},
		[threeCanvasRef, starsRef, isHoleCreationMode, isHoleDeletionMode, params, tab]
	);

	return {
		createClickHandler,
	};
};
